import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { GlowCard, SectionHeader, StatusPill } from '@norvix/ui';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import {
  reportTransactionsApi, type GroupTotals, type ReportCurrency, type ReportStatus, type TransactionReportRow,
} from '../../api/report-transactions.api.js';
import { APP_LOCALE } from '../../utils/locale.js';

import { ReportExportMenu, useReportExport } from './ReportExportMenu.js';
import { ReportPeriodFields, useReportPeriod } from './ReportPeriodControls.js';
import { amountStyle, effectiveRate, money, periodLabel } from './report-format.js';

/**
 * Reports → Transactions (DEV-129): one row per customer money movement with
 * the customer's commission on it. Provider cost and margin arrive with DEV-144.
 */

export const TYPE_LABEL: Record<TransactionReportRow['type'], string> = {
  SEPA: 'SEPA', SEPA_INSTANT: 'SEPA Instant', SWIFT: 'SWIFT', INTERNAL: 'Internal', FEE: 'Commission',
};
const STATUS_LABEL: Record<ReportStatus, string> = {
  COMPLETED: 'Completed', PROCESSING: 'Processing', REJECTED: 'Rejected', RETURNED: 'Returned',
  UNATTRIBUTED: 'Unattributed', EVIDENCE_ONLY: 'Evidence only',
};
const STATUS_TONE: Record<ReportStatus, 'success' | 'warning' | 'error' | 'neutral'> = {
  COMPLETED: 'success', PROCESSING: 'warning', REJECTED: 'error', RETURNED: 'error', UNATTRIBUTED: 'warning', EVIDENCE_ONLY: 'neutral',
};
const PROVIDER_LABEL: Record<string, string> = { GURUPAY: 'GuruPay', BANKERA: 'Bankera', MANUAL: 'Manual bank', NORVIX: 'Norvix' };
const FEE_SOURCE_LABEL: Record<TransactionReportRow['feeSource'], string> = {
  STANDARD: 'Standard', INDIVIDUAL: 'Individual', EDITED: 'Edited by staff', UNKNOWN: 'Unknown schedule', NONE: '',
};

interface Option { value: string; label: string }
const FILTERS: Array<{ key: string; label: string; options: Option[] }> = [
  { key: 'provider', label: 'Provider', options: Object.entries(PROVIDER_LABEL).map(([value, label]) => ({ value, label })) },
  { key: 'type', label: 'Type', options: Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label })) },
  { key: 'direction', label: 'Direction', options: [{ value: 'IN', label: 'Incoming' }, { value: 'OUT', label: 'Outgoing' }] },
  { key: 'flow', label: 'Flow', options: ['B2B', 'B2C', 'C2B', 'C2C'].map((value) => ({ value, label: value })).concat({ value: 'UNKNOWN', label: 'Unknown' }) },
  { key: 'status', label: 'Status', options: Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })) },
];

function GroupTable({ title, rows, currency, onPick, pickLabel }: { title: string; rows: GroupTotals[]; currency: string;
  onPick?: (key: string) => void; pickLabel?: string }): JSX.Element {
  return <Paper sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
    <TableContainer><Table size="small" aria-label={title}>
      <TableHead><TableRow><TableCell>Name</TableCell><TableCell align="right">Operations</TableCell>
        <TableCell align="right">Turnover</TableCell><TableCell align="right">Commissions</TableCell><TableCell align="right">Rate</TableCell></TableRow></TableHead>
      <TableBody>{rows.map((g) => <TableRow key={g.key} hover>
        <TableCell sx={{ maxWidth: 260 }}>
          {onPick && g.key !== 'UNATTRIBUTED'
            ? <Button size="small" onClick={() => onPick(g.key)} title={pickLabel} sx={{ p: 0, minWidth: 0, textAlign: 'left', justifyContent: 'flex-start', overflowWrap: 'anywhere' }}>{g.label}</Button>
            : <Typography variant="body2">{PROVIDER_LABEL[g.label] ?? g.label}</Typography>}
        </TableCell>
        <TableCell align="right" sx={amountStyle}>{g.count.toLocaleString(APP_LOCALE)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{money(g.turnoverMinor, currency)}</TableCell>
        <TableCell align="right" sx={{ ...amountStyle, fontWeight: 600 }}>{money(g.feeMinor, currency)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{effectiveRate(g.paymentFeeMinor, g.turnoverMinor)}</TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer>
  </Paper>;
}

function DetailsTable({ rows, currency }: { rows: TransactionReportRow[]; currency: string }): JSX.Element {
  return <TableContainer><Table aria-label="Transactions" sx={{ minWidth: 1250 }}>
    <TableHead><TableRow>
      <TableCell>Executed (UTC)</TableCell><TableCell>Type</TableCell><TableCell>Direction</TableCell><TableCell>Flow</TableCell>
      <TableCell align="right">Amount</TableCell><TableCell align="right">In {currency}</TableCell><TableCell>Customer</TableCell>
      <TableCell>Counterparty</TableCell><TableCell>Provider</TableCell><TableCell>Status</TableCell>
      <TableCell>Commission rate</TableCell><TableCell align="right">Commission</TableCell>
    </TableRow></TableHead>
    <TableBody>{rows.map((r) => {
      const when = r.executedAt ?? r.createdAt;
      return <TableRow key={`${r.source}:${r.id}`} hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{when.slice(0, 10)}<Typography variant="caption" display="block" color="text.secondary">{when.slice(11, 19)}</Typography></TableCell>
        <TableCell>{TYPE_LABEL[r.type]}</TableCell>
        <TableCell>{r.direction === 'IN' ? 'Incoming' : 'Outgoing'}</TableCell>
        <TableCell>{r.flow === null ? '—' : r.flow === 'UNKNOWN' ? 'Unknown' : r.flow}</TableCell>
        <TableCell align="right" sx={amountStyle}>{money(r.amountMinor, r.currency)}</TableCell>
        <TableCell align="right" sx={amountStyle}>{money(r.reportingMinor, currency)}</TableCell>
        <TableCell sx={{ maxWidth: 220 }}><Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{r.customerName || 'Not attributed'}</Typography>
          <Typography variant="caption" color="text.secondary">{r.customerType === 'B' ? 'Business' : r.customerType === 'C' ? 'Personal' : ''}</Typography></TableCell>
        <TableCell sx={{ maxWidth: 240 }}><Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{r.counterpartyName || '—'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{[r.counterpartyAccount, r.counterpartyCountry].filter(Boolean).join(' · ')}</Typography></TableCell>
        <TableCell>{PROVIDER_LABEL[r.provider] ?? r.provider}</TableCell>
        <TableCell><StatusPill tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</StatusPill></TableCell>
        <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2">{r.feeName ? (r.feeRate || '—') : '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{[FEE_SOURCE_LABEL[r.feeSource], r.feeScheduleVersion !== null ? `v${r.feeScheduleVersion}` : ''].filter(Boolean).join(' · ')}</Typography></TableCell>
        <TableCell align="right" sx={{ ...amountStyle, fontWeight: 600 }}>
          {r.feeMinor === null ? '—' : <Tooltip title={r.feeRefunded ? 'Refunded to the customer' : r.feeName}>
            <span style={r.feeRefunded ? { textDecoration: 'line-through' } : undefined}>{money(r.feeReportingMinor, currency)}</span></Tooltip>}
        </TableCell>
      </TableRow>;
    })}
    {rows.length === 0 && <TableRow><TableCell colSpan={12}>No transactions on this page.</TableCell></TableRow>}
    </TableBody>
  </Table></TableContainer>;
}

export function TransactionsReportTab(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const period = useReportPeriod();
  const { from, through } = period;
  const raw = params.get('currency');
  const currency: ReportCurrency = raw === 'USD' || raw === 'CAD' ? raw : 'EUR';
  const customerId = params.get('customerId') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const pageSize = [25, 50, 100].includes(Number(params.get('pageSize'))) ? Number(params.get('pageSize')) : 50;
  const update = (values: Record<string, string>, resetPage = true): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) { if (value) next.set(key, value); else next.delete(key); }
    if (resetPage) next.delete('page');
    setParams(next);
  };
  const validDates = period.error === null;
  const filterValues = Object.fromEntries(FILTERS.map((f) => [f.key, params.get(f.key) ?? '']));
  const query = new URLSearchParams({ from, through, currency, customerId, page: String(page), pageSize: String(pageSize),
    ...filterValues }).toString();
  const report = useQuery({ queryKey: ['report-transactions', query], queryFn: () => reportTransactionsApi.read(query),
    enabled: validDates, staleTime: 30_000, retry: 1 });
  const data = report.data;
  const exporter = useReportExport((format) => reportTransactionsApi.export(query, format),
    `norvix-transactions-${from}-${through}-${currency}`);
  const customerLabel = data?.byCustomer.find((c) => c.key === customerId)?.label ?? customerId;
  const anyFilter = customerId !== '' || Object.values(filterValues).some((v) => v !== '');

  return <Stack spacing={3} sx={{ minWidth: 0 }}>
    <Box>
      <SectionHeader title="Transactions" description="Every customer payment in the period with the commission charged on it." actions={
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} disabled={report.isFetching || !validDates} onClick={() => { void report.refetch(); }}>Refresh</Button>
          <ReportExportMenu exporter={exporter} disabled={!data || report.isFetching || !validDates} />
        </Stack>} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
        <ReportPeriodFields period={period} onChange={update} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">Show in</Typography>
          <ToggleButtonGroup exclusive size="small" color="primary" value={currency} aria-label="Reporting currency"
            onChange={(_, value: ReportCurrency | null) => { if (value) update({ currency: value }); }}>
            <ToggleButton value="EUR">EUR</ToggleButton><ToggleButton value="USD">USD</ToggleButton><ToggleButton value="CAD">CAD</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Box>

    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(5,minmax(0,1fr))' }, gap: 2 }}>
        {FILTERS.map((f) => <TextField key={f.key} select size="small" label={f.label} value={filterValues[f.key]}
          InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} onChange={(event) => update({ [f.key]: event.target.value })}>
          <MenuItem value="">All</MenuItem>
          {f.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>)}
      </Box>
      {anyFilter && <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        {customerId !== '' && <Chip label={`Customer: ${customerLabel}`} onDelete={() => update({ customerId: '' })} />}
        <Button size="small" onClick={() => update({ customerId: '', ...Object.fromEntries(FILTERS.map((f) => [f.key, ''])) })}>Clear filters</Button>
      </Stack>}
    </Paper>

    {period.error !== null && <Alert severity="warning">{period.error}</Alert>}
    {exporter.error && <Alert severity="error" onClose={exporter.clearError}>{exporter.error}</Alert>}
    {report.isError && <Alert severity="error">{report.error instanceof Error ? report.error.message : 'The report could not be loaded.'} Use Refresh to retry.</Alert>}
    {report.isLoading && <Stack direction="row" spacing={1.5} alignItems="center" role="status"><CircularProgress size={22} /><Typography>Loading transactions…</Typography></Stack>}
    {data && validDates && <>
      {report.isFetching && <LinearProgress aria-label="Refreshing transactions" />}
      {data.totals.missingRates > 0 && <Alert severity="warning">
        {data.totals.missingRates} {data.totals.missingRates === 1 ? 'amount has' : 'amounts have'} no eligible ECB rate, so converted totals are unavailable. Rows and the export still show the original amounts.
      </Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', xl: 'repeat(4,minmax(0,1fr))' }, gap: 2 }}>
        {[
          { label: 'Operations', value: data.totals.count.toLocaleString(APP_LOCALE), hint: 'Payments and stand-alone commissions' },
          { label: 'Turnover', value: money(data.totals.turnoverMinor, currency), hint: 'Completed payments, in and out' },
          { label: 'Customer commissions', value: money(data.totals.feeMinor, currency), hint: 'Charged, after refunds' },
          { label: 'Effective rate', value: effectiveRate(data.totals.paymentFeeMinor, data.totals.turnoverMinor), hint: 'Commissions on completed payments ÷ turnover' },
        ].map((tile) => <GlowCard key={tile.label} style={{ padding: 20, borderRadius: 12 }}>
          <Typography variant="body2" color="text.secondary">{tile.label}</Typography>
          <Typography sx={{ fontSize: { xs: 24, lg: 26 }, fontWeight: 700, fontVariantNumeric: 'tabular-nums', mt: 1, overflowWrap: 'anywhere' }}>{tile.value}</Typography>
          <Typography variant="caption" color="text.secondary">{tile.hint}</Typography>
        </GlowCard>)}
      </Box>

      {data.totalRows === 0 ? <Paper sx={{ p: 4 }}><Typography variant="h6">No transactions for this selection</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>Try a different period or clear the filters.</Typography></Paper> : <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,3fr) minmax(0,2fr)' }, gap: 3 }}>
          <GroupTable title="By customer" rows={data.byCustomer} currency={currency} pickLabel="Show only this customer"
            onPick={(key) => update({ customerId: key })} />
          <GroupTable title="By provider" rows={data.byProvider} currency={currency} />
        </Box>
        <Paper sx={{ overflow: 'hidden', minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
            <Typography variant="h6">Details</Typography><Chip size="small" label={`${data.totalRows} operations`} />
          </Stack>
          <DetailsTable rows={data.rows} currency={currency} />
          <TablePagination component="div" count={data.totalRows} page={page - 1} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, index) => update({ page: String(index + 1) }, false)} onRowsPerPageChange={(event) => update({ pageSize: event.target.value })}
            sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5, pl: 1 }, '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } } }} />
        </Paper>
      </>}
      <Typography variant="caption" color="text.secondary">
        Covers {periodLabel(from, through)} (UTC, inclusive). Updated {new Date(data.loadedAt).toLocaleString(APP_LOCALE)}. Amounts in {currency} use the ECB rate dated on or before the execution date (at most seven days old), as in Fees & Commissions.
        Provider costs and margin are not shown yet. The Excel and CSV exports have every column, including dates, references, banks and BICs.
      </Typography>
    </>}
  </Stack>;
}
